import {Checkbox, CheckboxGroup, Dialog, Flash, FormControl, TextInput} from '@primer/react'
import {useMutation} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useRef, useState} from 'react'
import type {Topic} from '../types'
import {TopicTagInput} from './TopicTagInput'
import styles from './EditRepositoryDetailsDialog.module.css'

const DESCRIPTION_CHAR_LIMIT = 350

interface EditRepositoryDetailsDialogProps {
  onClose: () => void
  onSaved: () => Promise<void>
  ownerLogin: string
  repoName: string
  description?: string
  website?: string
  topics?: Topic[]
  showReleasesSection: boolean
  showDeploymentsSection: boolean
  showPackagesSection: boolean
  pagesUrl?: string
}

export function EditRepositoryDetailsDialog({
  onClose,
  onSaved,
  ownerLogin,
  repoName,
  description: initialDescription,
  website: initialWebsite,
  topics: initialTopics,
  showReleasesSection,
  showDeploymentsSection,
  showPackagesSection,
  pagesUrl,
}: EditRepositoryDetailsDialogProps) {
  const descriptionRef = useRef<HTMLInputElement>(null)
  const [description, setDescription] = useState(initialDescription ?? '')
  const [website, setWebsite] = useState(initialWebsite ?? '')
  const [topicNames, setTopicNames] = useState<string[]>(initialTopics?.map(t => t.name) ?? [])
  const [usePagesUrl, setUsePagesUrl] = useState(!!pagesUrl && initialWebsite === pagesUrl)
  const [releasesEnabled, setReleasesEnabled] = useState(showReleasesSection)
  const [deploymentsEnabled, setDeploymentsEnabled] = useState(showDeploymentsSection)
  const [packagesEnabled, setPackagesEnabled] = useState(showPackagesSection)

  const charsRemaining = DESCRIPTION_CHAR_LIMIT - description.length

  const {mutate, isPending, error} = useMutation({
    mutationFn: async () => {
      const body = {
        repo_description: description,
        repo_homepage: usePagesUrl && pagesUrl ? pagesUrl : website,
        repo_topics: topicNames.length > 0 ? topicNames : [''],
        repo_sections: {
          releases: releasesEnabled ? '1' : '0',
          deployments: deploymentsEnabled ? '1' : '0',
          packages: packagesEnabled ? '1' : '0',
          ...(pagesUrl ? {pages_url: usePagesUrl ? '1' : '0'} : {}),
        },
      }

      let response: Response
      try {
        response = await verifiedFetchJSON(`/${ownerLogin}/${repoName}/settings/update_meta`, {
          method: 'PUT',
          body,
        })
      } catch {
        throw new Error('Error saving your changes.')
      }

      if (!response.ok) {
        let errorMessage = 'Error saving your changes.'
        try {
          const data = (await response.json()) as {error?: string}
          if (data.error) errorMessage = data.error
        } catch {
          // response body is not JSON — use default message
        }
        throw new Error(errorMessage)
      }
    },
    onSuccess: async () => {
      await onSaved()
      onClose()
    },
  })

  const handleSubmit = useCallback(() => mutate(), [mutate])

  return (
    <Dialog
      title="Edit repository details"
      width="xlarge"
      onClose={onClose}
      initialFocusRef={descriptionRef}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: onClose},
        {buttonType: 'primary', content: 'Save changes', onClick: handleSubmit, disabled: isPending},
      ]}
    >
      <div className={styles.dialogBody}>
        {error && (
          <Flash variant="danger" aria-live="assertive">
            {error.message}
          </Flash>
        )}

        <FormControl>
          <FormControl.Label>Description</FormControl.Label>
          <TextInput
            ref={descriptionRef}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Short description of this repository"
            maxLength={DESCRIPTION_CHAR_LIMIT}
            block
          />
          <FormControl.Caption className={`${styles.charCount} ${charsRemaining < 50 ? styles.warningCaption : ''}`}>
            <span aria-live="polite">{charsRemaining} characters remaining</span>
          </FormControl.Caption>
        </FormControl>

        <FormControl className={styles.formGroup}>
          <FormControl.Label>Website</FormControl.Label>
          {pagesUrl ? (
            <>
              <TextInput
                value={usePagesUrl ? pagesUrl : website}
                onChange={e => setWebsite(e.target.value)}
                placeholder="Enter a valid URL"
                type="url"
                block
                readOnly={usePagesUrl}
                className={usePagesUrl ? styles.readOnlyInput : ''}
              />
              <FormControl className={styles.pagesCheckbox}>
                <Checkbox
                  checked={usePagesUrl}
                  onChange={e => {
                    setUsePagesUrl(e.target.checked)
                    if (e.target.checked) setWebsite(pagesUrl)
                  }}
                />
                <FormControl.Label className="text-bold">Use your GitHub Pages website</FormControl.Label>
              </FormControl>
            </>
          ) : (
            <TextInput
              value={website}
              onChange={e => setWebsite(e.target.value)}
              placeholder="Enter a valid URL"
              type="url"
              block
            />
          )}
        </FormControl>

        <FormControl className={styles.formGroup}>
          <FormControl.Label id="topicAutocompleteLabel">Topics</FormControl.Label>
          <TopicTagInput topics={topicNames} onChange={setTopicNames} ownerLogin={ownerLogin} repoName={repoName} />
        </FormControl>

        <CheckboxGroup className={styles.formGroup}>
          <CheckboxGroup.Label className="text-bold">Include in the home page</CheckboxGroup.Label>
          <FormControl>
            <Checkbox checked={releasesEnabled} onChange={e => setReleasesEnabled(e.target.checked)} />
            <FormControl.Label>Releases</FormControl.Label>
          </FormControl>
          <FormControl>
            <Checkbox checked={deploymentsEnabled} onChange={e => setDeploymentsEnabled(e.target.checked)} />
            <FormControl.Label>Deployments</FormControl.Label>
          </FormControl>
          <FormControl>
            <Checkbox checked={packagesEnabled} onChange={e => setPackagesEnabled(e.target.checked)} />
            <FormControl.Label>Packages</FormControl.Label>
          </FormControl>
        </CheckboxGroup>
      </div>
    </Dialog>
  )
}
